(() => {
  'use strict';
  const config = document.querySelector('[data-sac-reward-auto-apply]');
  if (!config || window.SacCityRewardAutoApply) return;
  const root = new URL(config.dataset.cartRoot || '/', location.origin);
  const proxy = new URL(config.dataset.proxy || '/apps/sac-city-rewards', location.origin);
  if (root.origin !== location.origin || proxy.origin !== location.origin || !/^\/apps\/[a-z0-9_-]+\/?$/.test(proxy.pathname)) return;
  if (!root.pathname.endsWith('/')) root.pathname += '/';
  const cartUrl = new URL('cart.js', root);
  const updateUrl = new URL('cart/update.js', root);
  const checkoutPath = new URL('checkout', root).pathname;
  const codePattern = /^SC-[A-F0-9]{32}$/i;
  const discountInput = '[name*="discount" i], [name*="coupon" i], [name*="promo" i], [data-sac-discount-entry], .upcart-public-discount-code__input';
  const manualControl = '.upcart-public-discount-code__button, .upcart-public-cart-summary__discount-codes .upcart-public-component-tag__remove-button, [data-sac-discount-action]';
  const bypass = new WeakSet();
  let pending, manualOverride = false, uncertain = false, checkoutPending = false, registeredUpcart = false;
  function codesIn(cart, acceptedOnly = false) {
    const codes = new Set();
    for (const row of cart.discount_codes || []) if (typeof row.code === 'string' && (!acceptedOnly || row.applicable === true)) codes.add(row.code.trim());
    const applications = [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(row => row.discount_application))];
    for (const row of applications) if (row && row.type === 'discount_code' && typeof row.title === 'string') codes.add(row.title.trim());
    return [...codes].filter(Boolean);
  }
  const sameCode = (a, b) => a.toUpperCase() === b.toUpperCase();
  const hasCode = (codes, value) => codes.some(code => sameCode(code, value));
  const sameCodes = (a, b) => a.length === b.length && a.every(code => hasCode(b, code));
  function announce(status, cart) {
    const message = config.dataset[status] || '';
    document.dispatchEvent(new CustomEvent('sac-city:rewards-cart', {detail: {status, message, cart}}));
    const target = document.querySelector('[data-sac-reward-status]');
    if (target) { target.textContent = message; target.setAttribute('role', 'status'); }
  }
  function report(codes, cart, existing) {
    if (existing) return announce('existing', cart);
    if (!codes.length) return announce('cleared', cart);
    const accepted = codesIn(cart, true), count = codes.filter(code => hasCode(accepted, code)).length;
    announce(count === codes.length ? 'applied' : count ? 'partial' : 'ready', cart);
  }
  function refreshCartDisplay() {
    if (checkoutPending) return;
    try {
      if (typeof window.upcartRefreshCart === 'function') Promise.resolve(window.upcartRefreshCart()).catch(() => {});
      else {
        const items = document.querySelector('cart-items');
        if (typeof items?.refresh === 'function') Promise.resolve(items.refresh()).catch(() => {});
        else document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      }
    } catch {}
  }
  function waitForCartIdle(signal) {
    if (signal.aborted) return Promise.reject(new Error('CART_BUSY'));
    if (!window.SacLmCart?.state?.busy) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const cleanup = () => { document.removeEventListener('sac:liqui-cart:state', check); signal.removeEventListener('abort', cancel); };
      const check = () => { if (!window.SacLmCart?.state?.busy) { cleanup(); resolve(); } };
      const cancel = () => { cleanup(); reject(new Error('CART_BUSY')); };
      document.addEventListener('sac:liqui-cart:state', check);
      signal.addEventListener('abort', cancel, {once: true});
      check();
    });
  }
  async function apply() {
    if (pending) return pending;
    if (manualOverride || uncertain) return;
    pending = (async () => {
      const signal = AbortSignal.timeout(4000);
      const endpoint = new URL(proxy);
      endpoint.pathname = endpoint.pathname.replace(/\/$/, '') + '/auto-apply';
      endpoint.searchParams.set('_nonce', String(Date.now()));
      const response = await fetch(endpoint, {credentials: 'same-origin', cache: 'no-store', signal});
      if (!response.ok) return;
      const data = await response.json();
      if (manualOverride || !['READY', 'SIGN_IN_REQUIRED'].includes(data.status)) return;
      if (data.status === 'READY' && !Array.isArray(data.rewards)) return;
      const codes = data.status === 'READY' ? [...new Set(data.rewards.filter(row => typeof row.code === 'string' && codePattern.test(row.code) && Date.parse(row.expiresAt) > Date.now()).map(row => row.code))].slice(0, 2) : [];
      await waitForCartIdle(signal);
      if (manualOverride || window.SacLmCart?.state?.uncertain) return;
      const cartResponse = await fetch(cartUrl, {credentials: 'same-origin', cache: 'no-store', signal});
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      if (!cart || !Array.isArray(cart.items) || manualOverride) return;
      const existing = codesIn(cart), manualCodes = existing.filter(code => !codePattern.test(code));
      const desired = manualCodes.length ? manualCodes : codes;
      if (sameCodes(existing, desired)) { report(codes, cart, manualCodes.length > 0); return; }
      if (!desired.length && !existing.some(code => codePattern.test(code))) return;
      const confirmedResponse = await fetch(cartUrl, {credentials: 'same-origin', cache: 'no-store', signal});
      if (!confirmedResponse.ok) return;
      const confirmed = await confirmedResponse.json();
      if (!confirmed || !Array.isArray(confirmed.items) || !sameCodes(existing, codesIn(confirmed)) || manualOverride || window.SacLmCart?.state?.busy || window.SacLmCart?.state?.uncertain) return;
      let update;
      try {
        update = await fetch(updateUrl, {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({discount: desired.join(',')}), signal});
      } catch (error) { uncertain = true; throw error; }
      if (!update.ok) return;
      const updated = await update.json();
      if (!updated || !Array.isArray(updated.items)) { uncertain = true; return; }
      report(codes, updated, manualCodes.length > 0);
      refreshCartDisplay();
    })().catch(() => {}).finally(() => { pending = undefined; });
    return pending;
  }
  function replayAfterPending(event, target, action) {
    if (!pending || bypass.has(target)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    void pending.finally(() => { bypass.add(target); try { action(); } finally { bypass.delete(target); } });
  }
  function resumeCheckout(event, target, action) {
    if (bypass.has(target) || event.defaultPrevented) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending = true;
    void apply().finally(() => { checkoutPending = false; bypass.add(target); try { action(); } finally { bypass.delete(target); } });
  }
  window.SacCityRewardAutoApply = {apply, pause: () => { manualOverride = true; }};
  document.addEventListener('input', event => {
    if (event.target instanceof Element && event.target.matches(discountInput)) manualOverride = true;
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target, button = event.submitter;
    if (!(form instanceof HTMLFormElement) || bypass.has(form) || event.defaultPrevented) return;
    if (button?.getAttribute('name') !== 'checkout' && form.querySelector(discountInput)) {
      manualOverride = true;
      replayAfterPending(event, form, () => form.requestSubmit(button));
      return;
    }
    const action = new URL(form.action, location.origin);
    if (!button || button.getAttribute('name') !== 'checkout' || action.origin !== location.origin || !action.pathname.replace(/\/$/, '').endsWith('/cart')) return;
    resumeCheckout(event, form, () => form.requestSubmit(button));
  }, true);
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !(event.target instanceof Element)) return;
    const manual = event.target.closest(manualControl);
    if (manual && !bypass.has(manual)) { manualOverride = true; replayAfterPending(event, manual, () => manual.click()); return; }
    const link = event.target.closest('a[href]');
    if (!link || bypass.has(link) || link.target === '_blank' || link.hasAttribute('download')) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin || ![checkoutPath, '/checkout'].includes(url.pathname.replace(/\/$/, ''))) return;
    resumeCheckout(event, link, () => location.assign(url.href));
  }, true);
  function registerUpcart() {
    if (registeredUpcart || typeof window.upcartSubscribeCheckoutClicked !== 'function') return;
    registeredUpcart = true;
    window.upcartSubscribeCheckoutClicked(({event}) => {
      const target = event?.target instanceof Element ? event.target.closest('button, a') : null;
      if (target && !target.disabled) resumeCheckout(event, target, () => {
        if (target.isConnected === false) location.assign(new URL('checkout', root).href);
        else target.click();
      });
    });
  }
  window.addEventListener('pageshow', event => { registerUpcart(); if (event.persisted) void apply(); });
  window.addEventListener('load', registerUpcart);
  window.addEventListener('aftersell-upcart:public-events:cart-loaded', () => { registerUpcart(); void apply(); });
  window.addEventListener('aftersell-upcart:public-events:cart-updated', () => { registerUpcart(); if (!pending) void apply(); });
  document.addEventListener('on:cart:add', () => { void apply(); });
  document.addEventListener('on:line-item:change', () => { void apply(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void apply(); });
  registerUpcart();
  void apply();
})();
